import type { ActorRefFrom } from 'xstate';
import { notificationState } from '~/services/notifications/notification.machine';
import { providerService } from '~/services/provider/provider.service.machine';
import { websocketService } from '~/services/websocket';
import { alertView } from './alerts';
import { chatbotMachine } from './chatbot/chatbot.machine';
import { confirmationView } from './confirmation/confirmationView.machine';
import { providerAssociationMachine } from './employer/provider-associations/providerAssociation.machine';
import { employerRegistrationView } from './employer/registration-v2/registration-view.machine';
import { employerRegistrationService } from './employer/registration-v2/registration.service.machine';
import { marketContractsMachine } from './market/contracts/marketContracts.machine';
import { providerRegistrationView } from './provider/registration-v2/registration-view.machine';
import { employerAssociationMachine } from './provider/settings/employerAssociation.machine';
import { userState } from './user';
import { academicsMachine } from './vault/academics/academics.machine';
import { vaultState } from './vault/account/vaultState.machine';
import { aoiMachine } from './vault/aoi/aoi.machine';
import { attestationMachine } from './vault/attestations/attestation.machine';
import {
  boardCertMachine,
  tableMachine as BoardCertTableMachine,
} from './vault/board-certification/boardCert.machine';
import { tableMachine as certCrudMachine, certMachine } from './vault/certification/cert.machine';
import { cmeTrainingMachine } from './vault/cme-training/cmeTraining.machine';
import { documentsViewMachine } from './vault/documents/documents-view.machine';
import { documentsService } from './vault/documents/documents.machine';
import { employmentMachine } from './vault/employment/employment.machine';
import { licensesFormMachine } from './vault/licenses/licenseForm.machine';
import { malpracticeMachine } from './vault/malpractice/malpractice.machine';
import { medLicenseMachine } from './vault/medical-license/medical_license.machine';
import { peerRefReqMachine } from './vault/peer-reference-request/peerRefRequestMachine';
import { privilegesMachine } from './vault/privileges/privileges.machine';
import { profileFormMachine } from './vault/profile/profileForm.machine';

export const WEBSOCKET_SERVICE = 'webSocketService';
export const VAULT_SERVICE = 'vaultService';
export const PROVIDER_SERVICE = 'providerService';
export const EMPLOYER_SERVICE = 'employerService';
export const DOCUMENTS_SERVICE = 'documentsService';

export const ALERT_VIEW_STATE = 'alertViewState';
export const NOTIFICATION_VIEW_STATE = 'notificationViewState';
export const VAULT_STATE = 'vaultState';
export const ACADEMICS_STATE = 'academicsState';
export const AOI_STATE = 'aoiState';
export const ATTESTATION_STATE = 'attestationState';
export const BOARD_CERT_CRUD_STATE = 'boardCertCrudState';
export const BOARD_CERT_STATE = 'boardCertState';
export const CONFIRMATION_VIEW_STATE = 'confirmationViewState';
export const CERTIFICATION_STATE = 'certState';
export const CERTIFICATION_CRUD_STATE = 'certCrudState';
export const CHATBOT_STATE = 'chatbotState';
export const CME_TRAINING_STATE = 'cmeTrainingState';
export const DOCUMENTS_STATE = 'documentsState';
export const LICENSES_STATE = 'licensesState';
export const MALPRACTICE_STATE = 'malpracticeState';
export const MED_LICENSE_STATE = 'medLicenseState';
export const PEER_REFERENCE_STATE = 'peerReferenceState';
export const PRIVILEGES_STATE = 'privilegesState';
export const PROFILE_STATE = 'profileState';
export const EMPLOYMENT_STATE = 'employmentState';
export const EMPLOYER_ACCOUNT_STATE = 'employerAccountState';
export const EMPLOYER_ASSOCIATION_STATE = 'employerAssociationState';
export const MARKET_CONTRACTS_STATE = 'marketContractsState';
export const EMPLOYER_FACILITIES_STATE = 'employerFacilitiesState';
export const EMPLOYER_INFO_STATE = 'employerInfoState';
export const PROVIDER_ASSOCIATION_STATE = 'providerAssociationState';
export const NOTIFICATION_STATE = 'notificationState';

export const PROVIDER_REGISTRATION_VIEW = 'providerRegistrationView';

export const EMPLOYER_REGISTRATION_SERVICE = 'employerRegistrationService';
export const EMPLOYER_REGISTRATION_VIEW = 'employerRegistrationView';
export const NEGOTIATION_STATE = 'negotiationState';

export const USER_STATE = 'userState';

// Employer|Market System

export const MARKET_STATE = 'marketState';
export const MSAAS_LISTINGS_STATE = 'msaasListingsState';
export const JOB_LISTING_STATE = 'jobListingState';
export const MSAAS_LISTINGS_SEARCH_STATE = 'msaasListingsSearchState';
export const MSAAS_OFFERING_SEARCH_STATE = 'msaasOfferingSearchState';

export type MachineStates =
  | typeof VAULT_STATE
  | typeof ACADEMICS_STATE
  | typeof AOI_STATE
  | typeof ATTESTATION_STATE
  | typeof BOARD_CERT_CRUD_STATE
  | typeof BOARD_CERT_STATE
  | typeof CONFIRMATION_VIEW_STATE
  | typeof CERTIFICATION_STATE
  | typeof CERTIFICATION_CRUD_STATE
  | typeof CHATBOT_STATE
  | typeof CME_TRAINING_STATE
  | typeof DOCUMENTS_STATE
  | typeof DOCUMENTS_SERVICE
  | typeof PROVIDER_REGISTRATION_VIEW
  | typeof EMPLOYMENT_STATE
  | typeof EMPLOYER_ACCOUNT_STATE
  | typeof EMPLOYER_ASSOCIATION_STATE
  | typeof MARKET_CONTRACTS_STATE
  | typeof LICENSES_STATE
  | typeof MALPRACTICE_STATE
  | typeof MED_LICENSE_STATE
  | typeof PEER_REFERENCE_STATE
  | typeof PRIVILEGES_STATE
  | typeof PROFILE_STATE
  | typeof PROVIDER_ASSOCIATION_STATE
  | typeof EMPLOYER_REGISTRATION_SERVICE
  | typeof EMPLOYER_REGISTRATION_VIEW
  | typeof VAULT_SERVICE
  | typeof WEBSOCKET_SERVICE
  | typeof ALERT_VIEW_STATE
  | typeof NOTIFICATION_STATE
  | typeof PROVIDER_SERVICE
  | typeof USER_STATE;

export type ActorSystemMap = {
  [PROVIDER_SERVICE]: typeof providerService;
  [VAULT_STATE]: typeof vaultState;
  [ALERT_VIEW_STATE]: typeof alertView;
  [NOTIFICATION_STATE]: typeof notificationState;
  [ACADEMICS_STATE]: typeof academicsMachine;
  [AOI_STATE]: typeof aoiMachine;
  [ATTESTATION_STATE]: typeof attestationMachine;
  [BOARD_CERT_CRUD_STATE]: typeof BoardCertTableMachine;
  [BOARD_CERT_STATE]: typeof boardCertMachine;
  [CONFIRMATION_VIEW_STATE]: typeof confirmationView;
  [CERTIFICATION_STATE]: typeof certMachine;
  [CERTIFICATION_CRUD_STATE]: typeof certCrudMachine;
  [CHATBOT_STATE]: typeof chatbotMachine;
  [CME_TRAINING_STATE]: typeof cmeTrainingMachine;
  [DOCUMENTS_STATE]: typeof documentsViewMachine;
  [DOCUMENTS_SERVICE]: typeof documentsService;
  [EMPLOYMENT_STATE]: typeof employmentMachine;
  [LICENSES_STATE]: typeof licensesFormMachine;
  [MARKET_CONTRACTS_STATE]: typeof marketContractsMachine;
  [MALPRACTICE_STATE]: typeof malpracticeMachine;
  [MED_LICENSE_STATE]: typeof medLicenseMachine;
  [PEER_REFERENCE_STATE]: typeof peerRefReqMachine;
  [PRIVILEGES_STATE]: typeof privilegesMachine;
  [PROFILE_STATE]: typeof profileFormMachine;
  [WEBSOCKET_SERVICE]: typeof websocketService;
  // association
  [EMPLOYER_ASSOCIATION_STATE]: typeof employerAssociationMachine;
  [PROVIDER_ASSOCIATION_STATE]: typeof providerAssociationMachine;
  // registration
  [PROVIDER_REGISTRATION_VIEW]: typeof providerRegistrationView;
  [EMPLOYER_REGISTRATION_SERVICE]: typeof employerRegistrationService;
  [EMPLOYER_REGISTRATION_VIEW]: typeof employerRegistrationView;
  // system / user
  [USER_STATE]: typeof userState;
};
export type MachineStateIds = keyof ActorSystemMap;

/**
 * This creates a "system" type that can be used throughout the application Xstate scopes
 */

export interface AppActorSystem {
  get<K extends keyof ActorSystemMap>(id: K): ActorRefFrom<ActorSystemMap[K]>;
}
